<template>
  <div class="page-container">

    <!-- 헤더 -->
    <div class="page-header">
      <div class="page-title">
        현장 관리
        <span class="count-text">총 {{ resolvedTotalCount }} 개소</span>
      </div>

      <div class="header-buttons">
        <button class="btn primary" @click="$emit('create')">
          신규 등록
        </button>
      </div>
    </div>

    <!-- 본문 -->
    <div class="content-wrapper">

      <div class="card">

        <!-- 필터 영역 -->
        <div class="filter-section">
          <div class="filter-grid">

            <!-- 시/도 -->
            <div class="filter-item">
              <label class="filter-label">시/도</label>
              <select
                v-model="selectedSidoId"
                class="filter-input"
                @change="onChangeSido"
              >
                <option value="">전체</option>
                <option
                  v-for="s in sidoList"
                  :key="s.id"
                  :value="String(s.id)"
                >
                  {{ s.name }}
                </option>
              </select>
            </div>

            <!-- 시/군/구 -->
            <div class="filter-item">
              <label class="filter-label">시/군/구</label>
              <select v-model="selectedSigunguId" class="filter-input">
                <option value="">전체</option>
                <option
                  v-for="s in sigunguList"
                  :key="s.id"
                  :value="String(s.id)"
                >
                  {{ s.name }}
                </option>
              </select>
            </div>

            <!-- 대리점 -->
            <div class="filter-item">
              <label class="filter-label">대리점</label>
              <select v-model="selectedAgencyId" class="filter-input">
                <option value="">전체</option>
                <option
                  v-for="agency in agencyList"
                  :key="agency.id"
                  :value="String(agency.id)"
                >
                  {{ agency.name }}
                </option>
              </select>
            </div>

            <!-- 발주처 -->
            <div class="filter-item">
              <label class="filter-label">발주처</label>
              <select v-model="selectedClientId" class="filter-input">
                <option value="">전체</option>
                <option
                  v-for="client in clientList"
                  :key="client.id"
                  :value="String(client.id)"
                >
                  {{ client.name }}
                </option>
              </select>
            </div>

            <!-- 직접 입력 -->
            <div class="filter-item filter-item-keyword">
              <label class="filter-label">직접 입력</label>
              <input
                v-model.trim="keyword"
                class="filter-input"
                placeholder="현장명 / 주소 / 대리점 / 발주처 검색"
                @keyup.enter="onClickSearch"
              />
            </div>

            <!-- 버튼 -->
            <div class="filter-actions">
              <button class="btn primary" @click="onClickSearch" :disabled="loading">
                검색
              </button>
              <button class="btn ghost" @click="resetFilter" :disabled="loading">
                초기화
              </button>
            </div>

          </div>
        </div>

        <!-- 테이블 -->
        <div class="table-section">

          <table v-if="tableRows.length" class="data-table">
            <thead>
              <tr>
                <th>공사명</th>
                <th>대리점</th>
                <th>발주처</th>
                <th>주소지</th>
                <th>담당자</th>
                <th>연락처</th>
                <th>상태</th>
                <th class="text-center">작업</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in tableRows" :key="row.id">

                <td class="td-ellipsis" :title="row.siteName">
                  {{ row.siteName || '-' }}
                </td>

                <td class="td-ellipsis" :title="row.agencyName || '-'">
                  {{ row.agencyName || '-' }}
                </td>

                <td class="td-ellipsis" :title="row.clientName || '-'">
                  {{ row.clientName || '-' }}
                </td>

                <td class="td-ellipsis" :title="fullAddress(row)">
                  {{ fullAddress(row) }}
                </td>

                <td>{{ row.manager || '-' }}</td>

                <td>{{ row.phone || '-' }}</td>

                <td>
                  <span
                    class="site-status-badge"
                    :class="{
                      active: row.status === 'ACTIVE',
                      draft: row.status === '작성중'
                    }"
                  >
                    {{
                      row.status === 'ACTIVE'
                        ? '등록'
                        : row.status === '작성중'
                          ? '미등록'
                          : '-'
                    }}
                  </span>
                </td>

                <td class="td-actions">
                  <div class="row-actions">

                    <!-- 1줄 -->
                    <div class="action-main">
                      <button
                        class="btn xs primary site-action-btn"
                        @click="onClickDetail(row)"
                      >
                        측정량
                      </button>

                      <button
                        class="btn xs secondary site-action-btn"
                        @click="onClickCategory(row)"
                      >
                        카테고리
                      </button>

                      <button
                        class="btn xs success site-action-btn"
                        @click="onClickLoggerManage(row)"
                      >
                        센서관리
                      </button>
                    </div>

                    <!-- 2줄 -->
                    <div class="action-sub">
                      <button
                        class="btn xs outline site-action-btn"
                        @click="onClickEdit(row.id)"
                      >
                        관리
                      </button>

                      <button
                        class="btn xs danger site-action-btn"
                        @click="onClickDelete(row)"
                        :disabled="deletingSiteId === row.id"
                      >
                        {{ deletingSiteId === row.id ? '삭제중' : '삭제' }}
                      </button>
                    </div>

                  </div>
                </td>

              </tr>
            </tbody>
          </table>

          <div v-else class="table-empty">
            {{ loading ? '조회 중입니다...' : '표시할 데이터가 없습니다' }}
          </div>

        </div>

        <!-- 페이징 -->
        <PaginationBar
          :currentPage="currentPage"
          :totalPages="totalPages"
          :groupSize="groupSize"
          @change-page="goToPage"
        />

      </div>

    </div>
  </div>
</template>

<script>
import api from '@/api/axios'
import PaginationBar from '@/components/admin/PaginationBar.vue'

export default {
  name: 'AllSiteListComponent',

  components: {
    PaginationBar
  },

  emits: ['create', 'detail', 'edit', 'category', 'loggerManage'],

  data() {
    return {
      tableRows: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 0,
      size: 15,
      groupSize: 5,
      loading: false,
      deletingSiteId: null,

      // 필터
      sidoList: [],
      sigunguList: [],
      agencyList: [],
      clientList: [],

      selectedSidoId: '',
      selectedSigunguId: '',
      selectedAgencyId: '',
      selectedClientId: '',
      keyword: '',

      // 권한 기반 접근 가능 현장 ID 목록
      accessibleSiteIds: []
    }
  },

  computed: {
    resolvedTotalCount() {
      return this.totalCount || this.tableRows.length
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    async init() {
      await Promise.all([
        this.fetchSidoList(),
        this.fetchAgencyList(),
        this.fetchClientList(),
        this.fetchAccessibleSiteIds()
      ])

      await this.fetchSiteList()
    },

    async fetchAccessibleSiteIds() {
      try {
        const res = await api.get('/app/site/myList')
        const list = Array.isArray(res.data?.result) ? res.data.result : []

        this.accessibleSiteIds = list
          .map(item => Number(item.id))
          .filter(id => !Number.isNaN(id))
      } catch (e) {
        console.error('접근 가능 현장 목록 조회 실패', e)
        this.accessibleSiteIds = []
      }
    },

    async fetchSidoList() {
      try {
        const res = await api.get('/app/sido/list')
        this.sidoList = res.data?.result || []
      } catch (e) {
        console.error('시/도 목록 조회 실패', e)
        this.sidoList = []
      }
    },

    async fetchSigunguList() {
      if (!this.selectedSidoId) {
        this.sigunguList = []
        return
      }

      try {
        const res = await api.get('/app/sigungu/list', {
          params: {
            sidoId: this.selectedSidoId
          }
        })
        this.sigunguList = res.data?.result || []
      } catch (e) {
        console.error('시/군/구 목록 조회 실패', e)
        this.sigunguList = []
      }
    },

    async fetchAgencyList() {
      try {
        const res = await api.get('/app/agency/list', {
          params: {
            page: 0,
            size: 999,
            orderBy: 'nameAsc',
            deleted: false
          }
        })

        this.agencyList = res.data?.result?.content || []
      } catch (e) {
        console.error('대리점 목록 조회 실패', e)
        this.agencyList = []
      }
    },

    async fetchClientList() {
      try {
        const res = await api.get('/app/client/list', {
          params: {
            page: 0,
            size: 999,
            orderBy: 'nameAsc',
            deleted: false
          }
        })

        this.clientList = res.data?.result?.content || []
      } catch (e) {
        console.error('발주처 목록 조회 실패', e)
        this.clientList = []
      }
    },

    async fetchSiteList() {
      this.loading = true

      try {
        // 접근 가능한 현장이 하나도 없으면 빈 목록 처리
        if (!this.accessibleSiteIds.length) {
          this.tableRows = []
          this.totalCount = 0
          this.totalPages = 0
          this.currentPage = 0
          return
        }

        const filter = {
          page: this.currentPage,
          size: this.size,
          orderBy: 'createdAtDesc',
          deleted: false,

          // 서버에서 siteIds 조건 지원 필요
          siteIds: this.accessibleSiteIds.join(',')
        }

        if (this.keyword) {
          filter.keyword = this.keyword
        }

        if (this.selectedAgencyId) {
          filter.agencyId = this.selectedAgencyId
        }

        if (this.selectedClientId) {
          filter.clientId = this.selectedClientId
        }

        const selectedSido = this.sidoList.find(
          s => String(s.id) === String(this.selectedSidoId)
        )
        const selectedSigungu = this.sigunguList.find(
          s => String(s.id) === String(this.selectedSigunguId)
        )

        if (selectedSido) {
          filter.sidoName = selectedSido.name
        }

        if (selectedSigungu) {
          filter.sigunguName = selectedSigungu.name
        }

        const res = await api.get('/app/site/pagingList', { params: filter })
        const page = res.data?.result

        this.tableRows = Array.isArray(page?.content) ? page.content : []
        this.totalCount = Number(page?.totalElements || 0)
        this.totalPages = Number(page?.totalPages || 0)
        this.currentPage = Number(page?.number || 0)

        if (this.currentPage > 0 && this.tableRows.length === 0) {
          this.currentPage -= 1
          await this.fetchSiteList()
        }

      } catch (e) {
        console.error('현장 목록 조회 실패', e)
        this.tableRows = []
        this.totalCount = 0
        this.totalPages = 0
      } finally {
        this.loading = false
      }
    },

    async onChangeSido() {
      this.selectedSigunguId = ''
      await this.fetchSigunguList()
    },

    onClickSearch() {
      this.currentPage = 0
      this.fetchSiteList()
    },

    resetFilter() {
      this.selectedSidoId = ''
      this.selectedSigunguId = ''
      this.selectedAgencyId = ''
      this.selectedClientId = ''
      this.sigunguList = []
      this.keyword = ''
      this.currentPage = 0
      this.fetchSiteList()
    },

    goToPage(page) {
      if (page < 0 || page >= this.totalPages) return
      this.currentPage = page
      this.fetchSiteList()
    },

    fullAddress(row) {
      return [row.address, row.addressDetail].filter(Boolean).join(' ') || '-'
    },

    onClickDetail(row) {
      this.$emit('detail', row)
    },

    onClickEdit(id) {
      this.$emit('edit', id)
    },

    async onClickDelete(row) {
      const firstOk = confirm(
        `[${row.siteName}] 현장을 삭제하시겠습니까?\n\n삭제 시 목록에서 더 이상 표시되지 않습니다.`
      )
      if (!firstOk) return

      const secondOk = confirm(
        `정말로 삭제하시겠습니까?\n\n삭제 시 복구가 불가합니다.\n계속 진행하려면 확인을 눌러주세요.`
      )
      if (!secondOk) return

      this.deletingSiteId = row.id

      try {
        await api.delete('/app/site/delete', {
          data: { id: row.id }
        })

        alert('삭제 완료')
        await this.fetchAccessibleSiteIds()
        await this.fetchSiteList()

      } catch (e) {
        console.error('현장 삭제 실패', e)
        alert('삭제 실패')
      } finally {
        this.deletingSiteId = null
      }
    },

    onClickCategory(row) {
      this.$emit('category', row)
    },

    onClickLoggerManage(row) {
      this.$emit('loggerManage', row)
    },
  }
}
</script>

<style scoped>
@import "@/styles/admin/admin-layout.css";
@import "@/styles/admin/admin-table.css";
@import "@/styles/admin/admin-button.css";
@import "@/styles/admin/admin-card.css";
@import "@/styles/admin/admin-pagination.css";
@import "@/styles/admin/admin-badge.css";
@import "@/styles/admin/admin-input.css";

/* =========================
   FILTER
========================= */

.filter-section {
  margin-bottom: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px 12px;
  align-items: end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

.filter-input {
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d7dde5;
  border-radius: 10px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.filter-input:focus {
  outline: none;
  border-color: #e58a13;
  box-shadow: 0 0 0 4px rgba(229, 138, 19, 0.12);
}

select.filter-input {
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, #6b7280 50%),
    linear-gradient(135deg, #6b7280 50%, transparent 50%);
  background-position:
    calc(100% - 18px) calc(50% - 3px),
    calc(100% - 12px) calc(50% - 3px);
  background-size: 6px 6px;
  background-repeat: no-repeat;
  padding-right: 40px;
}

.filter-item-keyword {
  min-width: 0;
}

.filter-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  grid-column: 1 / -1;
  justify-self: end;
  min-width: 0;
}

.filter-actions .btn {
  height: 40px;
  min-width: 82px;
}

/* =========================
   TABLE
========================= */

.table-section {
  overflow: auto;
  border-radius: 14px;
}

.data-table {
  min-width: 1500px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
}

.data-table th {
  height: 48px;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  border-bottom: 1px solid #e9edf3 !important;
}

.data-table td {
  padding: 12px 10px;
  line-height: 1.45;
  text-align: center;
  vertical-align: middle;
  font-size: 13px;
  color: #1f2937;

  border-color: #edf1f5 !important;
}

.data-table tbody tr {
  transition: background-color 0.15s ease;
  height: 72px;
}

/* hover */
.data-table tbody tr:hover td {
  background: #f9fafb;
}

/* 좌측 active 라인 즉시 반응 */
.data-table tbody tr:hover td:first-child {
  box-shadow: inset 4px 0 0 #e58a13;
}

/* column width */

.data-table th:nth-child(1),
.data-table td:nth-child(1) {
  width: 220px;
}

.data-table th:nth-child(2),
.data-table td:nth-child(2) {
  width: 140px;
}

.data-table th:nth-child(3),
.data-table td:nth-child(3) {
  width: 140px;
}

.data-table th:nth-child(4),
.data-table td:nth-child(4) {
  width: 260px;
}

.data-table th:nth-child(5),
.data-table td:nth-child(5) {
  width: 90px;
}

.data-table th:nth-child(6),
.data-table td:nth-child(6) {
  width: 120px;
}

.data-table th:nth-child(7),
.data-table td:nth-child(7) {
  width: 90px;
}

.data-table th:nth-child(8),
.data-table td:nth-child(8) {
  width: 320px;
}

.td-actions {
  padding: 8px 10px;
  overflow: hidden;
}

/* 줄바꿈 허용 */
.td-ellipsis,
.data-table td:nth-child(1),
.data-table td:nth-child(2),
.data-table td:nth-child(3),
.data-table td:nth-child(4) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* =========================
   STATUS
========================= */

.site-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 58px;
  height: 28px;
  padding: 0 14px;

  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.site-status-badge.active {
  background: #eaf8ef;
  color: #16a34a;
}

.site-status-badge.draft {
  background: #fdecec;
  color: #ef4444;
}

/* =========================
   ACTION BUTTONS
========================= */

.row-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.action-main,
.action-sub {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
}

.site-action-btn {
  width: 74px;
  min-width: 74px;
  height: 30px;

  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;

  padding: 0 8px;
}

/* =========================
   EMPTY
========================= */

.table-empty {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 1600px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-item-keyword {
    grid-column: span 2;
  }
}

@media (max-width: 1024px) {
  .filter-grid {
    grid-template-columns: 1fr 1fr;
  }

  .filter-item-keyword {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-item-keyword,
  .filter-actions {
    grid-column: span 1;
  }

  .data-table {
    min-width: 1500px;
  }
}
</style>